import { useQuery } from '@tanstack/react-query';
import { DesignModelDto } from 'src/api/FiretrolApiClient';
import { designModelApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { hasPollTickFailed, toCancelToken } from 'src/api/queries/queryClient';
import { isConversionProcessing } from 'src/api/queries/designModels/useDesignModels';

const POLL_INTERVAL = 5000;

export interface UseDesignModelOptions {
  /** Poll the lightweight status endpoint while conversion is in progress. */
  poll?: boolean;
  enabled?: boolean;
}

/**
 * @registry Design model detail. `poll` drives conversion-status polling; the
 * expensive IFC payload is a separate query (`useDesignModelIfc`).
 */
export function useDesignModel(
  id: string | null | undefined,
  { poll = false, enabled = true }: UseDesignModelOptions = {},
) {
  return useQuery({
    queryKey: queryKeys.designModels.detail(id ?? ''),
    queryFn: ({ signal }) =>
      designModelApiClient.get(id!, toCancelToken(signal)),
    enabled: enabled && !!id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      if (!poll) return false;
      if (hasPollTickFailed(query.state)) return false;
      const model = query.state.data as DesignModelDto | undefined;
      return isConversionProcessing(model?.conversionStatus)
        ? POLL_INTERVAL
        : false;
    },
    meta: { silent: true, errorContext: 'design model' },
  });
}
